import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container, Typography, Button, Box, Paper,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField,
  Chip, Alert, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

const API = process.env.REACT_APP_API_URL;

export default function Empresas() {
  const { usuario } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    nombre: '',
    subdominio: '',
    admin_nombre: '',
    admin_email: '',
    admin_password: ''
  });

  const cargarEmpresas = () => {
    axios.get(`${API}/api/empresas`)
      .then(r => setEmpresas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEmpresas([]));
  };

  useEffect(() => { cargarEmpresas(); }, []);

  const crearEmpresa = async () => {
    try {
      await axios.post(`${API}/api/empresas`, {
        nombre: form.nombre,
        subdominio: form.subdominio.toLowerCase().replace(/\s/g, '-'),
        admin_nombre: form.admin_nombre,
        admin_email: form.admin_email,
        admin_password: form.admin_password
      });
      setOpen(false);
      setError('');
      setForm({ nombre: '', subdominio: '', admin_nombre: '', admin_email: '', admin_password: '' });
      cargarEmpresas();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear empresa');
    }
  };

  const toggleEmpresa = async (id) => {
    await axios.patch(`${API}/api/empresas/${id}/toggle`);
    cargarEmpresas();
  };

  const eliminarEmpresa = async (id) => {
    if (window.confirm('¿Eliminar esta empresa y todos sus datos?')) {
      await axios.delete(`${API}/api/empresas/${id}`);
      cargarEmpresas();
    }
  };

  if (usuario?.rol !== 'super_admin') {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">No tienes permisos para ver esta página.</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Empresas</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Nueva Empresa
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><b>Nombre</b></TableCell>
              <TableCell><b>Subdominio</b></TableCell>
              <TableCell><b>URL</b></TableCell>
              <TableCell><b>Estado</b></TableCell>
              <TableCell><b>Acciones</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {empresas.map(e => (
              <TableRow key={e.id}>
                <TableCell>{e.nombre}</TableCell>
                <TableCell>{e.subdominio}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="primary">
                    {e.subdominio}.geo-energy-tech.com
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={e.activa ? 'Activa' : 'Inactiva'}
                    color={e.activa ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={e.activa ? <ToggleOffIcon /> : <ToggleOnIcon />}
                      color={e.activa ? 'warning' : 'success'}
                      onClick={() => toggleEmpresa(e.id)}
                    >
                      {e.activa ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => eliminarEmpresa(e.id)}
                    >
                      Eliminar
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {empresas.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay empresas registradas
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Nueva Empresa</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>
            Datos de la empresa
          </Typography>
          <TextField
            fullWidth label="Nombre empresa" value={form.nombre}
            onChange={e => setForm({ ...form, nombre: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth label="Subdominio" value={form.subdominio}
            onChange={e => setForm({ ...form, subdominio: e.target.value })}
            helperText={`URL: ${form.subdominio || 'subdominio'}.geo-energy-tech.com`}
            sx={{ mb: 2 }}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Administrador local
          </Typography>
          <TextField
            fullWidth label="Nombre admin" value={form.admin_nombre}
            onChange={e => setForm({ ...form, admin_nombre: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth label="Email admin" type="email" value={form.admin_email}
            onChange={e => setForm({ ...form, admin_email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth label="Contraseña admin" type="password" value={form.admin_password}
            onChange={e => setForm({ ...form, admin_password: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={crearEmpresa}>Crear</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}